import { type TimeFormat } from '../time-format';

/** A wall-clock time as numbers — the hour on a 24-hour scale, the way a zone reports it. */
export interface ClockParts {
  /** 0–23. */
  hour: number;
  /** 0–59. */
  minute: number;
}

/**
 * The day-period markers a 12-hour clock is rendered with.
 *
 * Injected for the same reason `formatDatePattern` takes its names: this module
 * is shared by the api, the admin and React Native, and Hermes ships a reduced
 * ICU. Each caller supplies its locale's pair.
 */
export interface ClockMeridiem {
  am: string;
  pm: string;
}

const pad = (value: number): string => (value < 10 ? `0${String(value)}` : String(value));

/**
 * Renders a wall-clock time under `brand.timeFormat`.
 *
 * The one clock formatter, the counterpart of `formatDatePattern`: `24h` is
 * `HH:mm`; `12h` is `h:mm` followed by the period, with midnight and noon read
 * as 12 rather than 0 — the two hours a naive `hour % 12` gets wrong.
 */
export function formatClock(
  parts: ClockParts,
  timeFormat: TimeFormat,
  meridiem: ClockMeridiem,
): string {
  if (timeFormat === '24h') {
    return `${pad(parts.hour)}:${pad(parts.minute)}`;
  }

  const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
  const period = parts.hour < 12 ? meridiem.am : meridiem.pm;
  return `${String(hour12)}:${pad(parts.minute)} ${period}`;
}
